import { useRef, useState, useEffect } from 'react';
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area';
import { isDayboardOverlayLocked, subscribeDayboardOverlayLock } from '@/lib/ui/dayboardOverlayLock';

interface DayboardScrollAreaProps {
  children: React.ReactNode;
  className?: string;
}

export const DayboardScrollArea = ({ children, className }: DayboardScrollAreaProps) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [startY, setStartY] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const [scrollTop, setScrollTop] = useState(0);
  // Track the outer vertical scroll container (page or nearest scrollable ancestor)
  const scrollContainerRef = useRef<HTMLElement | null>(null);
  const windowStartYRef = useRef(0);
  const containerStartScrollTopRef = useRef(0);
  const rafIdRef = useRef<number | null>(null);

const handleMouseDown = (e: React.MouseEvent) => {
  // Only activate drag if not clicking on interactive elements
  if (e.button !== 0) return; // left-click only
  // A modal is open above the board: its drags are its own, never the board's.
  if (isDayboardOverlayLocked()) return;
  const target = e.target as HTMLElement;
  // React events bubble along the COMPONENT tree: a dialog portaled to <body>
  // from inside a card (e.g. the Guided Start bay picker) still reaches this
  // handler. Only a press that physically starts on the board may drag it.
  if (!e.currentTarget.contains(target)) return;
  if (
    target.tagName === 'BUTTON' ||
    target.closest('button') ||
    target.closest('[role="button"]') ||
    target.closest('input') ||
    target.closest('select') ||
    target.closest('textarea')
  ) {
    return;
  }

  const viewport = scrollRef.current?.querySelector('[data-radix-scroll-area-viewport]') as HTMLElement;
  if (!viewport) return;

  const rect = viewport.getBoundingClientRect();

  setIsDragging(true);
  setStartX(e.clientX - rect.left);
  setStartY(e.clientY - rect.top);
  setScrollLeft(viewport.scrollLeft);
  setScrollTop(viewport.scrollTop);

  viewport.style.cursor = 'grabbing';
  viewport.style.userSelect = 'none';
};

  const handleMouseMove = (e: MouseEvent) => {
    if (!isDragging) return;
    // ...nor continue one that began before the modal opened.
    if (isDayboardOverlayLocked()) { handleMouseUp(); return; }
    e.preventDefault();

    const viewport = scrollRef.current?.querySelector('[data-radix-scroll-area-viewport]') as HTMLElement;
    if (!viewport) return;

    const rect = viewport.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const walkX = (x - startX);
    const walkY = (y - startY);

    if (rafIdRef.current) cancelAnimationFrame(rafIdRef.current);
    rafIdRef.current = requestAnimationFrame(() => {
      viewport.scrollLeft = scrollLeft - walkX;
      viewport.scrollTop = scrollTop - walkY;
    });
  };

  const handleMouseUp = () => {
    setIsDragging(false);
    if (rafIdRef.current) {
      cancelAnimationFrame(rafIdRef.current);
      rafIdRef.current = null;
    }
    const viewport = scrollRef.current?.querySelector('[data-radix-scroll-area-viewport]') as HTMLElement;
    if (!viewport) return;

    viewport.style.cursor = 'grab';
    viewport.style.userSelect = '';
  };

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, startX, startY, scrollLeft, scrollTop]);

  useEffect(() => {
    const viewport = scrollRef.current?.querySelector('[data-radix-scroll-area-viewport]') as HTMLElement;
    if (viewport) {
      viewport.style.cursor = 'grab';
    }
  }, []);

  // A modal opening mid-drag ends the board drag at once.
  useEffect(() => {
    if (!isDragging) return;
    return subscribeDayboardOverlayLock(() => { if (isDayboardOverlayLocked()) handleMouseUp(); });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDragging]);

  return (
    <ScrollArea ref={scrollRef} className={className} onMouseDown={handleMouseDown}>
      {children}
      <ScrollBar orientation="horizontal" />
      <ScrollBar orientation="vertical" />
    </ScrollArea>
  );
};
